// hooks/useMessageHandlers.ts
import { useCallback } from 'react';

import {
  type Message,
  useChatOverlayContext,
} from '../context/chatOverlayContext';
import {
  type AskApiRequest,
  type AskApiResponse,
  type SampleDataApiRequest,
  type SampleDataAPIResponse,
} from '../data-layer/types';
import { addMessageToHistory } from '../services/chatHistory';
import { useInvertedLocalize } from '../utils/localization';
import { invertedMessages } from '../utils/messages';

const useMessageHandlers = () => {
  const { updateChatOverlayState } = useChatOverlayContext();

  const l = useInvertedLocalize(invertedMessages);

  const updateAutomatedUserMessageInSession = useCallback(
    (message: keyof typeof invertedMessages, isDividerRequired?: boolean) => {
      if (message.length > 0) {
        const userMessage: Message = {
          id: `${Date.now()}-user`,
          text: l(message),
          isUser: true,
          timestamp: Date.now(),
          chatuuid: null,
          isDividerRequired: isDividerRequired ?? false,
        };

        updateChatOverlayState('messages', addMessageToHistory(userMessage));
      }
    },
    [updateChatOverlayState, l]
  );

  const updateUserMessageInSession = useCallback(
    (request: AskApiRequest | SampleDataApiRequest) => {
      if (typeof request === 'object' && 'freeText' in request) {
        const userMessage: Message = {
          id: `${Date.now()}-user`,
          text: request.freeText,
          isUser: true,
          timestamp: Date.now(),
          chatuuid: request.chatuuid,
        };

        updateChatOverlayState('messages', addMessageToHistory(userMessage));
      }
    },
    [updateChatOverlayState]
  );

  const updateApiResponseInSession = useCallback(
    (response: AskApiResponse | SampleDataAPIResponse) => {
      const botMessage: Message = {
        id: `${Date.now()}-bot`,
        data: response, // JSON data from the bot response
        isUser: false,
        timestamp: Date.now(),
        chatuuid: response.chatuuid,
      };

      updateChatOverlayState('messages', addMessageToHistory(botMessage));
    },
    [updateChatOverlayState]
  );

  const updateAutomatedApiResponseInSession = useCallback(
    (
      message: keyof typeof invertedMessages,
      options?: {
        isSaveToChart: boolean;
      }
    ) => {
      const botMessage: Message = {
        id: `${Date.now()}-bot`,
        data: {
          contentType: 'text',
          text: l(message),
          chatuuid: message,
        }, // JSON data from the bot response
        isUser: false,
        timestamp: Date.now(),
        chatuuid: null,
        ...(options?.isSaveToChart && { isSaveToChart: options.isSaveToChart }),
      };

      updateChatOverlayState('messages', addMessageToHistory(botMessage));
    },
    [l, updateChatOverlayState]
  );

  return {
    updateUserMessageInSession,
    updateApiResponseInSession,
    updateAutomatedUserMessageInSession,
    updateAutomatedApiResponseInSession,
  };
};

export default useMessageHandlers;
